
import React, { useState } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import Home from './pages/Home';
import GetStarted from "./pages/getStarted";
import Login from "./pages/Login"
import Signup from "./pages/Signup"
import ResetPassword from "./pages/ResetPassword"
import DoctorDashboard from './pages/DoctorDashboard';
import OutpatientJoin from './pages/OutpatientJoin';
import PaymentSuccess from "./pages/PaymentSuccess";
import PaymentCancel from "./pages/PaymentCancel";
import Dashboard from './components/Dashboard';
import SerialAudioHandler from './components/SerialAudioHandler';
import VoiceEnroll from './pages/VoiceEnroll';
import VoiceEnrollStaff from './pages/VoiceEnrollStaff';
import DeleteAccount from './pages/DeleteAccount';
import AudioAnalysisTest from './pages/AudioAnalysisTest';
import Chatbot from './pages/Chatbot';
import Plans from './pages/Plans';
import Profile from './pages/Profile';
import ProfileEdit from './pages/ProfileEdit';
import Settings from './pages/Settings';
import Help from './pages/Help';
import './App.css';
import { TranslationProvider } from './hooks/TranslationProvider';
import { Navigate, useNavigate } from 'react-router-dom';

// Route guard: if logged in, redirect to '/' so GetStarted never shows for authenticated users
function GuardedGetStarted() {
  try {
    const isLoggedIn = typeof window !== 'undefined' && !!localStorage.getItem('neuro_user_id');
    if (isLoggedIn) return <Navigate to="/" replace />;
  } catch { /* ignore */ }
  return <GetStarted />;
}

// Standalone /login route. Login.jsx is authored as a modal component
// (it returns null when `open` is false), so the route supplies `open`
// plus a real close target. Modal usage elsewhere is unchanged.
function LoginPage() {
  const navigate = useNavigate();
  const isLoggedIn =
    typeof window !== 'undefined' && !!localStorage.getItem('neuro_user_id');
  if (isLoggedIn) return <Navigate to="/" replace />;
  return (
    <Login
      open
      onClose={() => navigate('/')}
      onLoginSuccess={() => navigate('/')}
    />
  );
}

function App() {
  // Pi Ring serial state — lifted here so the widget persists across page navigation
  const [serialPort, setSerialPort] = useState(null);
  const [ringPaired, setRingPaired] = useState(false);
  const [userId, setUserId] = useState(() => localStorage.getItem('neuro_user_id'));
  const [ringSaveBanner, setRingSaveBanner] = useState(null); // null | 'saved' | 'failed'

  const ringCallbacks = {
    onSerialConnected: (port) => setSerialPort(port),
    onRingPaired: () => {
      setRingPaired(true);
      setUserId(localStorage.getItem('neuro_user_id'));
    },
    onRingUnpaired: () => { setRingPaired(false); setSerialPort(null); },
  };

  return (
    <>
      {/* NEW: Wrap the entire app with TranslationProvider for global multi-language support */}
      <TranslationProvider> {/* NEW: Begin TranslationProvider wrapper */}
        <Router>
          <Routes>
            <Route path="/" element={<Home {...ringCallbacks} />} />
            <Route path="/getStarted" element={<GuardedGetStarted />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/signup" element={<Signup />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/doctor-dashboard" element={<DoctorDashboard />} />
            <Route path="/join" element={<OutpatientJoin />} />
            <Route path="/payment-success" element={<PaymentSuccess />} />
            {/* Render agency dashboard through Home layout so header/bot stay visible */}
            <Route path="/dashboard/take-care-agency" element={<Home {...ringCallbacks} />} />
            <Route path="/payment-cancel" element={<PaymentCancel />} />
            <Route path="/voice-enroll" element={<VoiceEnroll />} />
            <Route path="/voice-enroll-staff" element={<VoiceEnrollStaff />} />
            <Route path="/delete" element={<DeleteAccount />} />
            <Route path="/audio-test" element={<AudioAnalysisTest />} />
            <Route path="/chatbot" element={<Chatbot />} />
            <Route path="/plans" element={<Plans />} />
            <Route path="/profile" element={<Profile />} />
            <Route path="/profile/edit" element={<ProfileEdit />} />
            <Route path="/settings" element={<Settings />} />
            <Route path="/help" element={<Help />} />
          </Routes>

          {/* SerialAudioHandler lives OUTSIDE <Routes> so it persists on every page */}
          {ringPaired && serialPort && (
            <SerialAudioHandler
              userId={userId}
              serialPort={serialPort}
              onAudioReceived={(result) => {
                console.log('[App] Pi Ring audio processed, auto_saved:', result.auto_saved);
                setRingSaveBanner(result.auto_saved ? 'saved' : 'failed');
                setTimeout(() => setRingSaveBanner(null), 4000);
              }}
              onError={(err) => console.error('[App] Pi Ring serial error:', err)}
            />
          )}

          {/* Save-result banner — visible on every page */}
          {ringSaveBanner && (
            <div style={{
              position: 'fixed', top: 20, left: '50%', transform: 'translateX(-50%)',
              background: ringSaveBanner === 'saved' ? '#2e7d32' : '#c62828',
              color: '#fff', padding: '10px 24px', borderRadius: 8,
              fontWeight: 600, fontSize: '0.95rem', zIndex: 99999,
              boxShadow: '0 4px 16px rgba(0,0,0,0.35)',
            }}>
              {ringSaveBanner === 'saved'
                ? '✓ Pi Ring recording saved to Dashboard!'
                : '✗ Pi Ring: analysis done but save failed — check console'}
            </div>
          )}
        </Router>
      </TranslationProvider> {/* NEW: End TranslationProvider wrapper */}
    </>
  );
}

export default App;
